import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { ReactComponent as ExamIcon } from '../../../assets/icons/dashboard-exam.svg';
import { ReactComponent as SheetIcon } from '../../../assets/icons/dashboard-sheet.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/dashboard-calendar.svg';
import CardHeader from '@mui/material/CardHeader';
import { Divider, Grid } from '@mui/material';
import { useStyles } from '../Dashboard.styles';
import { ButtonOne } from '../../../components/ButtonOne/ButtonOne.component';
import Typography from '@mui/material/Typography';
import { DashboardCardProgressbar } from '../Progressbar/DashboardCardProgressbar.component';
import { RgzChipStatus } from '../RgzChipStatus/RgzChipStatus.component';
import { ExaminerFunction } from '../../Assessment/Assessment.types';
import { useHistory } from 'react-router-dom';
import {
  getCurrentProcess,
  getCurrentPzeAsExaminerProcess,
  getLowestProcessForPzeAsExaminer,
  setAssessmentSessionProcess,
} from '../../../utils/assessmentSessionProcess';
import { getAssessmentUrl, getSampleAssessmentRepositoryUrl, getSampleAssessmentUrl } from '../../../utils/routerUrls';
import { toTitleCase } from '../../../utils/toTitleCase';
import { DashboardNotifications } from '../DashboardNotifications/DashboardNotifications.component';
import useSessionUser from '../../../hooks/useSessionUser';
import { countryFormat } from '../../../const/formatDate';
import ActionsMenu from '../../../components/ActionsMenu/ActionsMenu.component';
import { ActionMenuItem } from '../../../components/ActionsMenu/ActionsMenu.types';
import { SessionsParametersDialog } from '../../Sessions/SessionsParametersDialog/SessionsParametersDialog.component';
import { AssessmentSessionState, DashboardTile, SessionType, TileState, TileType } from '../Dashboard.types';
import { OverflowTypograpy } from '../../../components/OverflowTypography/OverflowTypography.component';
import HideSourceIcon from '@mui/icons-material/HideSource';
import { SignalRContext } from '../../../Context/SignalR.context';
import { ServerOperations } from '../../../models/signalR.types';
import { ConversationType, Priority } from '../../../models/communication.types';
import { hasUnreadMessages } from '../../../services/communication/communication.service';
import { UnreadMessagePrompt } from '../../../components/Prompt/UnreadMessage/UnreadMessagePrompt.component';
import { getSheetCodeWithTaskGroupName } from '../../../components/Prompt/UnreadMessage/helpers';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import TooltipOne from '../../../components/TooltipOne/TooltipOne.component';
import { assessmentSessionActions } from '../../../reducers/assessmentSessionReducer/assessmentSessionSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import TooltipOneComponent from '../../../components/TooltipOne/TooltipOne.component';
import { useLayoutContext } from '../../../Context/Layout.context';
import { fillBlue } from '../../../utils/contrastSvgAttributes';
import { AssessmentSessionProcessTypeId } from '../../../services/exams/assessmentSession/assessments.types';

export const ExamTaskGroupSolutionCard: React.FC<{
  assessmentSession: DashboardTile;
  suspended?: boolean;
  refresh?: () => void;
}> = ({ assessmentSession, suspended, refresh }) => {
  const styles = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const prevStoredSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalActiveTab, setModalActiveTab] = useState<string>('');
  const [unreadMessagePrompt, setUnreadMessagePrompt] = useState<{
    isOpen: boolean;
    unreadConversationType: ConversationType;
  }>({ isOpen: false, unreadConversationType: ConversationType.Standard });

  const { highContrastMode } = useLayoutContext();

  const isZwoSession = assessmentSession.sessionType === SessionType.SampleAssessmentSession;
  const examName = toTitleCase(assessmentSession?.dashboardExamName);
  const sheetCode = assessmentSession?.dashboardSheetCode;
  const taskGroupName = toTitleCase(assessmentSession?.examTaskGroupName);
  const startDate = assessmentSession?.assesmentSessionStartDate
    ? new Date(assessmentSession?.assesmentSessionStartDate).toLocaleDateString(countryFormat)
    : '';
  const endDate = assessmentSession?.assesmentSessionEndDate
    ? new Date(assessmentSession?.assesmentSessionEndDate).toLocaleDateString(countryFormat)
    : '';
  const assessmentSessionDateRange = `${startDate} – ${endDate}`;
  const sessionStatus: AssessmentSessionState = assessmentSession?.assessmentSessionStateId;
  const isNotInProgress = isZwoSession
    ? false
    : sessionStatus === AssessmentSessionState.New || sessionStatus === AssessmentSessionState.Closed;
  const isRgzReadOnly = assessmentSession.tileState === TileState.ReadOnly;
  const isSuspended = suspended || assessmentSession.tileState === TileState.Suspended;
  const isRgzNotSent = false;
  const isRgzToBeDownloaded = false; //icon informing RGZ to be sent
  const isSpecialCase = assessmentSession.isAnySpecialCaseInAssessmentSession;
  const examinersBlocked = 0; //icon informing about Examiner blocked
  const pzeBlocked = 0; //icon informing about PZE blocked

  const total = assessmentSession?.sessionProcessStats.totalTaskGroupSolutionCount || 0;

  const zwoRGZCountSum =
    assessmentSession.sessionProcessSampleAssessmentGroupStats.sampleAssessmentRequiredSolutionsCountSum ?? 0;
  const zwoRGZAssessed =
    assessmentSession.sessionProcessSampleAssessmentGroupStats
      .sampleExamTaskGroupSolutionScoreAcceptanceStatusCzwoAssessed ?? 0;
  const zwoRGZAcceptanceStatusNone =
    assessmentSession.sessionProcessSampleAssessmentGroupStats.sampleExamTaskGroupSolutionScoreAcceptanceStatusNone ??
    0;
  const zwoRGZPzwoAccepted =
    assessmentSession.sessionProcessSampleAssessmentGroupStats
      .sampleExamTaskGroupSolutionScoreAcceptanceStatusPzwoAccepted ?? 0;

  const zwoRgzToBeSent = Math.max(zwoRGZCountSum - zwoRGZAssessed, 0);
  const rgzSent = assessmentSession.sessionProcessStats.sendedBackTaskGroupSolutionCount ?? 0;
  const rgzToBeSent = Math.max(total - rgzSent, 0);

  const { userAssessmentSessionInfo, reloadAssessmentSession } = useSessionUser();

  const isVerifier = assessmentSession?.isVerifier;
  const verifiedCount = assessmentSession.sessionProcessStats?.verifiedCount;
  const leftForVerificationCount = assessmentSession.sessionProcessStats?.leftForVerificationCount;

  const hasAdminSessionStatusManagePermission =
    assessmentSession.examinerFunction !== null &&
    [ExaminerFunction.KK, ExaminerFunction.KGZ].includes(assessmentSession?.examinerFunction);

  // TODO TAKE this info from API - history not yet implemented
  const pzwoRgzProgress = 1;

  const textGridArea = () => {
    switch (sessionStatus) {
      case AssessmentSessionState.New:
        return 'Oczekiwanie na rozpoczęcie sesji...';
      case AssessmentSessionState.Opened:
        return 'Sesja w trakcie';
      case AssessmentSessionState.Closed:
        return 'Sesja zakończona';
    }
  };

  const isPZEAsExaminer = () => {
    return assessmentSession.tileType === TileType.PZE_RGZ;
  };

  const isCzwoCze = () => {
    return [ExaminerFunction.CZWO, ExaminerFunction.CZE].includes(assessmentSession.examinerFunction);
  };

  const handleTokenGeneration = async (forceReloadToken?: boolean) => {
    if (userAssessmentSessionInfo?.assessmentSessionId !== assessmentSession.assessmentSessionId || forceReloadToken) {
      await reloadAssessmentSession(assessmentSession.assessmentSessionId, isPZEAsExaminer());
    }
  };

  const currentProcess = getCurrentProcess(assessmentSession.assessmentSessionProcesses);

  const getExaminerFunctionLabel = (tileType: TileType) => {
    switch (tileType) {
      case TileType.KK:
        return 'KK';
      case TileType.KGZ:
        return 'KGZ';
      case TileType.PZE:
        return 'PZE';
      case TileType.E:
      case TileType.CZWO:
        return 'RGZ';
      case TileType.PZWO:
        return 'PZWO';
      default:
        return 'RGZ';
    }
  };

  const proceedToSession = (session: DashboardTile, zwoSession?: boolean) => {
    SignalRContext.invoke(ServerOperations.JoinedAssessmentSession, session.assessmentSessionId);
    if (zwoSession) {
      if (session.examinerFunction === ExaminerFunction.KK) {
        history.push(getSampleAssessmentUrl(session.assessmentSessionId));
      } else {
        history.push(getSampleAssessmentRepositoryUrl());
      }
    } else if (isPZEAsExaminer()) {
      const tileTpe = getExaminerFunctionLabel(session.tileType);
      const process =
        tileTpe === 'RGZ' && session.isQualificationProcessActive
          ? getLowestProcessForPzeAsExaminer(assessmentSession.assessmentSessionProcesses)
          : getCurrentPzeAsExaminerProcess(assessmentSession.assessmentSessionProcesses);
      history.push(getAssessmentUrl(session.assessmentSessionId, process.toString()));
    } else {
      history.push(getAssessmentUrl(session.assessmentSessionId, currentProcess.toString()));
    }
  };

  const chooseSession = async (session: DashboardTile, zwoSession?: boolean) => {
    await handleTokenGeneration(session.tileType !== prevStoredSession?.tileType);
    dispatch(assessmentSessionActions.updateAssessmentSession(assessmentSession));
    setAssessmentSessionProcess(assessmentSession.assessmentSessionProcesses);
    hasUnreadMessages(Priority.Medium)
      .then((result) => {
        if (result.status === 204) {
          //no content
          proceedToSession(session, zwoSession);
        } else if (result.status === 200) {
          setUnreadMessagePrompt({ isOpen: true, unreadConversationType: result.data });
        }
      })
      .catch(() => proceedToSession(session, zwoSession));
  };

  const getActionsMenuIcon = (): JSX.Element => {
    const items: ActionMenuItem[] = [];

    const isDisabled =
      assessmentSession.examinerFunction === ExaminerFunction.KGZ &&
      !assessmentSession.isControlProcessActive &&
      !assessmentSession.isQualificationProcessActive;

    if (hasAdminSessionStatusManagePermission) {
      items.push({
        text: 'Ustaw parametry sesji oceniania',
        onClick: async () => {
          await handleTokenGeneration();
          assessmentSession.examinerFunction === ExaminerFunction.KK
            ? setModalActiveTab('')
            : assessmentSession.isControlProcessActive
            ? setModalActiveTab('Proces kontrolny')
            : setModalActiveTab('Proces kwalifikacji');
          setOpenModal(true);
        },
        disabled: isDisabled,
        tooltip: isDisabled ? 'Dla tej sesji nie ustawiono procesu kontrolnego ani kwalifikacyjnego' : '',
      });
    }
    return (
      <ActionsMenu
        menuItems={items}
        isNotInTable={true}
        iconStyle={{
          color: fillBlue,
        }}
      />
    );
  };

  const backgroundButton = !isRgzReadOnly ? '#3C419A' : undefined;
  const colorButton = !isRgzReadOnly ? 'white' : undefined;

  const cardHeaderTitle = (): JSX.Element => {
    return (
      <Grid container alignContent="center" alignItems="center" justifyContent="space-between">
        <Grid item className={styles.sessionName}>
          <OverflowTypograpy sx={{ fontWeight: 600, fontSize: '16px' }}>{taskGroupName}</OverflowTypograpy>
        </Grid>
        <Grid item className={styles.sessionDuration}>
          <CalendarIcon className={styles.calendarIcon} style={{ fill: highContrastMode ? '#FFFFFF' : '"#3C419A' }} />
          <strong>Czas trwania sesji:</strong> {assessmentSessionDateRange}
          {hasAdminSessionStatusManagePermission && !isZwoSession && (
            <span style={{ marginLeft: 2 }}>{getActionsMenuIcon()}</span>
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          marginLeft: -1,
          marginTop: '1rem',
          marginBottom: '1rem',
          width: 782,
          height: 169,
          color: '#2C2D3E',
          fontSize: '14px',
          fontWeight: 400,
          textAlign: 'left',
        }}
      >
        <CardHeader
          sx={{
            backgroundColor: 'rgba(202, 202, 252, 0.4)',
            color: '#2C2D3E',
            height: '0.2vh',
          }}
          title={cardHeaderTitle()}
        />
        {assessmentSession && (
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <div className={styles.itemContainer}>
                  <TooltipOneComponent title="Przedmiot (typ arkusza)" placement="top">
                    <ExamIcon
                      className={styles.examIcon}
                      style={{
                        fill: highContrastMode ? '#FFFFFF' : '#81818D',
                        fillOpacity: highContrastMode ? '1' : '0.8',
                      }}
                    />
                  </TooltipOneComponent>
                  <TooltipOneComponent title={examName} placement="top">
                    <span className={styles.sidebar}>{examName}</span>
                  </TooltipOneComponent>
                </div>
                <div className={styles.itemContainer}>
                  <TooltipOneComponent title="Kod arkusza" placement="top">
                    <SheetIcon
                      className={styles.margin}
                      style={{
                        fill: highContrastMode ? '#FFFFFF' : '#81818D',
                        fillOpacity: highContrastMode ? '1' : '0.8',
                      }}
                    />
                  </TooltipOneComponent>
                  <TooltipOneComponent title={sheetCode} placement="top">
                    <span className={styles.sidebar}>{sheetCode}</span>
                  </TooltipOneComponent>
                </div>
                <DashboardNotifications
                  pzeBlocked={pzeBlocked}
                  examinersBlocked={examinersBlocked}
                  isSpecialCase={!isZwoSession && !isCzwoCze() ? isSpecialCase : false}
                  isRgzToBeDownloaded={isRgzToBeDownloaded}
                  isRgzNotSent={isRgzNotSent}
                />
              </Grid>
              <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.75 }} />
              <Grid sx={{ alignItems: 'center' }} item xs={7}>
                {!isNotInProgress ? (
                  <RgzChipStatus
                    processState={currentProcess}
                    sessionType={assessmentSession.sessionType}
                    sampleAssessmentSessionStateId={assessmentSession.sampleAssessmentSessionStateId}
                  />
                ) : (
                  <div style={{ height: 25 }}></div>
                )}
                <div style={{ marginTop: '5px' }}>
                  <span className={styles.progressButtonContainer}>
                    {!isNotInProgress && (
                      <>
                        {assessmentSession.sessionType === SessionType.AssessmentSession ? (
                          <>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, marginRight: 1, marginTop: '1px' }}>
                              RGZ
                            </Typography>
                            <DashboardCardProgressbar
                              rgzAlreadySent={isVerifier && verifiedCount != null ? verifiedCount : rgzSent}
                              rgzToBeSent={
                                isVerifier && leftForVerificationCount != null ? leftForVerificationCount : rgzToBeSent
                              }
                              pzwoAlreadysent={isZwoSession ? pzwoRgzProgress : 0}
                              total={
                                isVerifier && verifiedCount != null && leftForVerificationCount != null
                                  ? verifiedCount + leftForVerificationCount
                                  : total
                              }
                              notStarted={false}
                            />
                          </>
                        ) : (
                          <>
                            {zwoRGZCountSum !== 0 && (
                              <>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, marginRight: 1, marginTop: '1px' }}>
                                  RGZ
                                </Typography>
                                <DashboardCardProgressbar
                                  rgzAlreadySent={zwoRGZAssessed}
                                  rgzToBeSent={zwoRgzToBeSent}
                                  pzwoAlreadysent={isZwoSession ? zwoRGZPzwoAccepted : 0}
                                  total={zwoRGZCountSum}
                                  totalZwo={zwoRGZCountSum}
                                  notStarted={false}
                                />
                                <TooltipOne title="Wymagane RGZ ZWO" placement="top">
                                  <InfoOutlinedIcon
                                    htmlColor="rgba(44, 45, 62, 0.8)"
                                    style={{
                                      verticalAlign: 'middle',
                                      margin: '3px 8px',
                                      width: '15px',
                                    }}
                                  />
                                </TooltipOne>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {isNotInProgress && (
                      <Typography
                        sx={{
                          fontSize: 16,
                          margin: 1,
                          color: '#3C419A',
                          fontWeight: 700,
                          marinBottom: -10,
                          marginRight: 'auto',
                        }}
                      >
                        {textGridArea()}
                      </Typography>
                    )}
                    <span>
                      {isSuspended ? (
                        <div style={{ display: 'flex', width: '100px' }}>
                          <HideSourceIcon style={{ color: 'red', paddingTop: '10px' }} />
                          <Typography style={{ color: 'red', fontWeight: 600 }}>SESJA ZAWIESZONA</Typography>
                        </div>
                      ) : (
                        <TooltipOneComponent
                          title={
                            assessmentSession.tileType === TileType.KK && !assessmentSession.isExamSheetConfigured
                              ? 'Arkusz nieskonfigurowany'
                              : ''
                          }
                          placement="top"
                        >
                          <span>
                            <ButtonOne
                              color="primary"
                              style={{
                                background: backgroundButton,
                                boxShadow: !isRgzReadOnly
                                  ? '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px'
                                  : undefined,
                                color: colorButton,
                                marginRight: -45,
                                width: 106,
                              }}
                              variant="outlined"
                              size="small"
                              disabled={
                                assessmentSession.tileState === TileState.Inactive ||
                                (assessmentSession.tileType === TileType.KK && !assessmentSession.isExamSheetConfigured)
                              }
                              onClick={() => chooseSession(assessmentSession, isZwoSession)}
                              aria-label={'Przycisk kierujący do listy RGZtów'}
                            >
                              {getExaminerFunctionLabel(assessmentSession.tileType)}
                            </ButtonOne>
                          </span>
                        </TooltipOneComponent>
                      )}
                    </span>
                  </span>
                </div>
                {!isNotInProgress && (
                  <div className={styles.gridContainer}>
                    <span className={styles.statisticContainer}>
                      {assessmentSession.sessionType === SessionType.SampleAssessmentSession ? (
                        <>
                          <Typography sx={{ fontSize: 14, marginRight: 1, fontWeight: 600, color: '#347F42' }}>
                            {zwoRGZAssessed}
                          </Typography>
                          <Typography sx={{ fontSize: 14, marginRight: 1 }}>Odesłane przez CZWO</Typography>
                          <Typography sx={{ fontSize: 14, marginRight: 1, fontWeight: 600, color: 'rgb(60, 65, 154)' }}>
                            {zwoRGZPzwoAccepted}
                          </Typography>
                          <Typography sx={{ fontSize: 14, marginRight: 1 }}>Zaakceptowane przez PZWO</Typography>
                        </>
                      ) : (
                        <>
                          {isZwoSession && (
                            <>
                              <Typography sx={{ fontSize: 14, marginRight: 1, fontWeight: 600, color: '#3C419A' }}>
                                {pzwoRgzProgress}
                              </Typography>
                              <Typography sx={{ fontSize: 14, marginRight: 1 }}>PZWO</Typography>
                            </>
                          )}
                          <Typography sx={{ fontSize: 14, marginRight: 1, fontWeight: 600, color: '#347F42' }}>
                            {isVerifier && verifiedCount !== null ? verifiedCount : rgzSent}
                          </Typography>
                          <Typography sx={{ fontSize: 14, marginRight: 1 }}>
                            {isVerifier && verifiedCount !== null ? 'Zweryfikowane' : 'Odesłane'}
                          </Typography>
                          <Typography sx={{ fontSize: 14, marginRight: 1, fontWeight: 600, color: '#7B62D5' }}>
                            {isVerifier && leftForVerificationCount !== null ? leftForVerificationCount : rgzToBeSent}
                          </Typography>
                          <Typography sx={{ fontSize: 14, marginLeft: '-4px' }}>
                            {isVerifier && leftForVerificationCount !== null
                              ? 'Pozostało do weryfikacji'
                              : 'Pozostało do oceny'}
                          </Typography>
                        </>
                      )}
                    </span>

                    {isZwoSession && (
                      <div style={{ width: '87%' }}>
                        <Divider sx={{ marginTop: 0.8 }} />
                        <Typography sx={{ fontSize: '0.8rem', marginTop: 0.5, fontWeight: 400, color: '#2C2D3E' }}>
                          {assessmentSession.examinerFunction !== ExaminerFunction.CZWO
                            ? 'Przypisane i nieodesłane RGZ dla CZWO: '
                            : 'Moje przypisane i nieodesłane RGZ: '}
                          <strong>{zwoRGZAcceptanceStatusNone}</strong>
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Card>
      <SessionsParametersDialog
        xCloseButton={() => setOpenModal(false)}
        onClose={() => setOpenModal(false)}
        id="sessionsParametersDialog"
        open={openModal}
        sheetId={assessmentSession.examSheetId}
        title={''}
        examinerFunction={assessmentSession.examinerFunction}
        activeTab={modalActiveTab}
        isExaminerView={true}
        isSheetConfigured={true}
        isControlProcessActive={assessmentSession.isControlProcessActive}
        isQualificationProcessActive={assessmentSession.isQualificationProcessActive}
        refresh={refresh}
      />
      {unreadMessagePrompt.isOpen && (
        <UnreadMessagePrompt
          action={'enterSession'}
          onConfirm={(url: string) => history.push(url)}
          unreadConversationType={unreadMessagePrompt.unreadConversationType}
          sheetCodeWithTaskGroupName={getSheetCodeWithTaskGroupName(
            assessmentSession.dashboardSheetCode,
            assessmentSession.examTaskGroupName,
          )}
        />
      )}
    </>
  );
};
